// Reactive answer-state + derived progress/path computeds for the sign-up flow, split out
// of useSignupFlow (the orchestrator) so the state lives in one focused place. This owns NO
// side-effects and NO control flow — the orchestrator composes it with the timeline,
// tracking, and API composables and drives all transitions. See useSignupFlow.
import { ref, computed } from 'vue'
import { resolvePath, pathKey as computePathKey, type StepKey } from '../config/flow-graph'
import { STEP_ANNOUNCEMENTS, signupIntro } from '../config/wordings'
import { resolveSuccess } from '../config/success-wordings'
import { characterByKey } from '../config/characters'
import type { SignupState } from './useSignupTracking'
import type { AddressGeo } from './useAddressAutocomplete'

export type Phase = 'chat' | 'success'
export type SignupSubPhase = 'intro' | 'consent' | 'method-choice' | 'email-form' | 'oauth-google'

export function useSignupState() {
  // ── Answer state (the V1 slice of the proto store) ────────────
  const universe = ref<string | null>(null)
  const firstInterest = ref<string | null>(null)
  const role = ref<string | null>(null)
  const subProfile = ref<string | null>(null)
  const adresse = ref('')
  const villeLabel = ref('')
  const addressGeo = ref<AddressGeo | null>(null) // captured LocationIQ set, threaded into registration
  const typeGarde = ref<string | null>(null)
  const formData = ref<Record<string, unknown>>({})
  const authMethod = ref<string | null>(null)
  const cguAccepted = ref(false)
  const characterKey = ref('ines')
  // Email-path credentials, captured at submitEmail and threaded to createAccount.
  const email = ref('')
  const password = ref('')
  // Backend post-signup redirect (RegistrationSerializer#redirectUrl) — the source of
  // truth for the success screen's primary CTA; config dests are static fallbacks only.
  const accountRedirectUrl = ref<string | null>(null)

  // ── Navigation / phase ────────────────────────────────────────
  const stepKey = ref<StepKey>('universe')
  const phase = ref<Phase>('chat')
  const signupSubPhase = ref<SignupSubPhase>('intro')

  // ── Transient UI state ────────────────────────────────────────
  const submitInFlight = ref(false) // per-phase anti-double-submit
  const availabilityCount = ref<number | null>(null)
  // Inline error shown on the email form (e.g. the address is already registered) —
  // keeps the form open with a login link instead of proceeding. Cleared on re-submit.
  const emailError = ref<string | null>(null)

  // ── Derived ───────────────────────────────────────────────────
  const activeChar = computed(() => characterByKey(characterKey.value))
  const pathKey = computed(() =>
    computePathKey({ universe: universe.value as never, role: role.value as never }),
  )
  const currentPath = computed(() =>
    resolvePath({
      universe: universe.value as never,
      role: role.value as never,
      subProfile: subProfile.value as never,
    }),
  )
  const stepIdx = computed(() => currentPath.value.indexOf(stepKey.value) + 1)
  const totalSteps = computed(() => currentPath.value.length)
  const totalKnown = computed(() => !!pathKey.value)
  // `signup` is the terminal step of every person path (org paths end in `mn-longform`), so
  // stepIdx === totalSteps there → the bar fills to 100%. It used to be hard-capped at 85% (a
  // remnant of the prototype's 85→95→100 auth sub-phase progression that no longer exists),
  // which left the bar ~85% full while the label already read "N/N".
  const progressPct = computed(() => (stepIdx.value / totalSteps.value) * 100)
  const showProgress = computed(() => true)
  const stepAnnouncement = computed(() => STEP_ANNOUNCEMENTS[stepKey.value] || '')
  const signupIntroBubble = computed(() => signupIntro(pathKey.value))
  const successConfig = computed(() =>
    resolveSuccess({ pathKey: pathKey.value, subProfile: subProfile.value }),
  )

  // Immutable slice passed to tracking events.
  function snapshot(): SignupState {
    return {
      universe: universe.value,
      firstInterest: firstInterest.value,
      role: role.value,
      subProfile: subProfile.value,
      typeGarde: typeGarde.value,
      authMethod: authMethod.value,
    }
  }

  return {
    // answer state
    universe,
    firstInterest,
    role,
    subProfile,
    adresse,
    villeLabel,
    addressGeo,
    typeGarde,
    formData,
    authMethod,
    cguAccepted,
    characterKey,
    email,
    password,
    accountRedirectUrl,
    // navigation / phase
    stepKey,
    phase,
    signupSubPhase,
    // transient
    submitInFlight,
    availabilityCount,
    emailError,
    // derived
    activeChar,
    pathKey,
    currentPath,
    stepIdx,
    totalSteps,
    totalKnown,
    progressPct,
    showProgress,
    stepAnnouncement,
    signupIntroBubble,
    successConfig,
    // helpers
    snapshot,
  }
}
